import React, { useRef, useState, useEffect, useContext } from 'react';
import defaultProfilePic from "../../../assets/defaultProfilePic.svg";
import { GlobalContext } from "../../../App.tsx";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import {useAppStore} from "../../../GlobalStore/GlobalStore.ts";
import {writeSettings} from "../../../Utils/db/users/userSettings/writeSettings.ts";

interface UserProfileDropdownProps {}

export const getProviderField = (user: any, fieldName: string): string | null => {
    if (!user.providerData || user.providerData.length === 0) {
        return user[fieldName] || null;
    }

    // Priority order: google.com -> apple.com -> password
    const providers = ['google.com', 'apple.com', 'password'];

    for (const providerId of providers) {
        const provider = user.providerData.find((p: any) => p.providerId === providerId);
        if (provider && provider[fieldName]) {
            // Special case for Apple email relay
            if (fieldName === 'email' && providerId === 'apple.com' &&
                provider.email.includes('privaterelay.appleid.com')) {
                continue;
            }
            return provider[fieldName];
        }
    }

    // Fallback to top-level field if no provider data found
    return user[fieldName] || null;
};

const getUserEmail = (user: any): string => {
    return getProviderField(user, 'email') || '';
};

const getUserDisplayName = (user: any): string => {
    return getProviderField(user, 'displayName') || '';
};

const getUserPhotoURL = (user: any): string => {
    return getProviderField(user, 'photoURL') || '';
};

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = () => {
    const { resetStateHelper, messagesSequenceRef } = useContext(GlobalContext);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const { isIncognito } = settingsObj;
    const userObj = useAppStore((state: any) => state.userObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleIncognitoClick = () => {
        const newIncognitoState = !isIncognito;
        writeSettings(userObj.user, {incognitoMode: newIncognitoState}, environmentObj.environment);
        setSettingsObj({
            isIncognito: newIncognitoState
        });
        setDynamicStateObj({conversationId: null})
        if (!newIncognitoState && messagesSequenceRef.current.length !== 0) {
            setDynamicStateObj({visiblePopup: 'askToSave'})
        } else {
            resetStateHelper();
        }
    };

    const handleSettingsClick = () => {
        setDynamicStateObj({visiblePopup: 'settings', isMenuOpen: false});
    };

    const email = getUserEmail(userObj.user);
    const displayName = getUserDisplayName(userObj.user);
    const photoURL = getUserPhotoURL(userObj.user);

    return (
        <div className="mb-2 relative profile-dropdown" ref={dropdownRef}>
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex w-60 md:w-64 items-center p-2 rounded-md bg-neutral-300 dark:bg-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors duration-200"
            >
                <img
                    src={photoURL || defaultProfilePic}
                    alt="Profile"
                    className="w-8 h-8 rounded-full mr-3"
                />
                <div className="flex flex-col items-start overflow-hidden">
                    <span className="text-sm text-neutral-700 dark:text-white font-medium truncate w-full">{displayName}</span>
                    <span className={`${!displayName || displayName.trim() === "" ? "text-sm" : "text-xs"} text-neutral-500 dark:text-neutral-400 truncate w-full`}>{email}</span>
                </div>
            </button>
            {showDropdown && (
                <div
                    className="absolute w-60 md:w-64 bottom-full mb-1 bg-neutral-100 dark:bg-neutral-800 rounded-md overflow-hidden shadow-xl z-10">
                    <div
                        className="pl-4 pr-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex justify-between items-center cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleIncognitoClick();
                        }}
                    >
                        <span>Incognito</span>
                        <label className="relative inline-flex items-center cursor-pointer"
                               onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isIncognito}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleIncognitoClick();
                                }}
                            />
                            <div
                                className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:bg-cyan-600 after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:rounded-full after:h-3.5 after:w-3.5 after:transition-all dark:border-gray-600"></div>
                        </label>
                    </div>
                    <button
                        onClick={handleSettingsClick}
                        className="w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex justify-between items-center"
                    >
                        <span>Settings</span>
                        <FontAwesomeIcon icon={faCog}/>
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserProfileDropdown;